// src/components/admin/SliderManagement.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../../api/api";
import ConfirmationModal from "../common/ConfirmationModal";

const BACKEND_URL = "https://imagensdaface.com.br";
const TRANSITION_DURATION_MS = 800; // Mantido para referência

// Ícones simples para botões na tabela
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zm-3.536 3.536l-9.192 9.192H3v-2.121l8.586-8.586 2.121 2.121z" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m-4-6h4m4-6H5" /></svg>;

// ===================================
// OPÇÕES
// ===================================
const transitionOptions = [
    { value: "fade", label: "Fade (Desvanecer)" },
    { value: "slide-left", label: "Slide (Esquerda)" },
    { value: "slide-right", label: "Slide (Direita)" },
    { value: "cube", label: "Cube (Giro 3D)" },
    { value: "flip", label: "Flip (Virar)" },
    { value: "zoom-in", label: "Zoom In" },
    { value: "stack", label: "Stack (Empilhar)" },
];

const animationOptions = [
    { value: "none", label: "Nenhuma" },
    { value: "fly-in-left", label: "Voar da Esquerda" },
    { value: "zoom-in", label: "Zoom Rápido" },
    { value: "typewriter", label: "Máquina de Escrever" },
    { value: "whip", label: "Chicote" },
];

const fitOptions = ["cover", "contain", "fill", "none"];

const getStatusBadge = (status) => {
    const base = "px-3 py-1 text-xs font-semibold rounded-full";
    switch (status) {
        case 'Publicado':
            return `${base} bg-green-100 text-green-800`;
        case 'Rascunho':
            return `${base} bg-gray-200 text-gray-800`;
        default:
            return `${base} bg-yellow-100 text-yellow-800`;
    }
}

const getFullImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("blob:")) return url;
    if (url.startsWith("http")) return url;
    return `${BACKEND_URL}/${url.replace(/^\/+/, "")}`;
};

// ===================================
// COMPONENTE PRINCIPAL
// ===================================

export default function SliderManagement({ setNotification }) {
    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const [editingSlide, setEditingSlide] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [slideToDelete, setSlideToDelete] = useState(null);

    const mountedRef = useRef(true);
    
    const [previewKey, setPreviewKey] = useState(0); 
    const transitionTimeoutRef = useRef(null);


    const fetchSlides = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/slider/admin");
            const data = res?.data?.slides || [];
            const normalized = data.map((s) => ({
                id: s.id,
                title: s.title ?? "",
                subtitle: s.subtitle ?? "",
                display_text: s.display_text ?? "",
                image_url: s.image_url ?? "",
                link_url: s.link_url ?? "",
                button_text: s.button_text ?? "Acessar",
                status: s.status ?? "Rascunho",
                slide_order: Number(s.slide_order) || 999,
                
                transition_effect: s.transition_effect || "fade", 
                border_radius_px: Number(s.border_radius_px) || 16,
                animation_type: s.animation_type || "none",
                text_animation_duration_ms: Number(s.text_animation_duration_ms) || 500,
                
                duration_ms: Number(s.duration_ms) || 5000,
                overlay_color: s.overlay_color || "#000000",
                overlay_opacity: Number(s.overlay_opacity ?? 0.35),
                image_fit: s.image_fit || "cover",
                subtitle_color: s.subtitle_color || "#ffffff",
                subtitle_font: s.subtitle_font || "inherit",
                subtitle_size: Number(s.subtitle_size) || 18,
            }));
            if (mountedRef.current) setSlides(normalized);
        } catch (err) {
            console.error("Erro ao carregar slides:", err);
            setNotification?.({ show: true, type: "error", message: "Falha ao carregar slides." });
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [setNotification]);

    useEffect(() => {
        mountedRef.current = true;
        fetchSlides();
        return () => {
            mountedRef.current = false;
        };
    }, [fetchSlides, refreshTrigger]);

    // Função para iniciar a simulação da transição
    const triggerPreviewTransition = useCallback(() => {
        if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
        }
        
        setPreviewKey(prev => prev + 1);

        transitionTimeoutRef.current = setTimeout(() => {
            setPreviewKey(0); 
        }, 1800); 

    }, []);

    // Hook para disparar o preview
    useEffect(() => {
        if (editingSlide && (editingSlide.image_url || editingSlide.transition_effect || editingSlide.animation_type || editingSlide.text_animation_duration_ms)) {
            triggerPreviewTransition();
        }
        return () => {
            if (transitionTimeoutRef.current) {
                clearTimeout(transitionTimeoutRef.current);
            }
        };
    }, [editingSlide?.transition_effect, editingSlide?.animation_type, editingSlide?.text_animation_duration_ms, editingSlide?.image_url, triggerPreviewTransition]);


    // Upload helper
    const uploadImage = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return res.data?.file?.url || res.data?.url || res.data?.filepath || null;
    };

    // Create or update slide
    const handleCreateOrUpdate = async () => {

        setIsSaving(true);
        try {
            let finalImage = editingSlide.image_url;

            if (editingSlide.new_image_file) {
                const uploaded = await uploadImage(editingSlide.new_image_file);
                if (!uploaded) throw new Error("Falha no upload da imagem.");
                finalImage = uploaded;
            }

            const payload = {
                title: editingSlide.title?.trim() || null,
                subtitle: editingSlide.subtitle?.trim() || null,

                display_text: editingSlide.display_text?.trim() || null,

                image_url: editingSlide.delete_image ? null : finalImage || null,

                link_url: editingSlide.link_url?.trim() || null,
                button_text: editingSlide.button_text?.trim() || null,

                status: editingSlide.status || "Rascunho",
                slide_order: Number(editingSlide.slide_order) || 999,

                transition_effect: editingSlide.transition_effect || "fade",
                border_radius_px: Number(editingSlide.border_radius_px) || 16,
                animation_type: editingSlide.animation_type || "none",
                text_animation_duration_ms: Number(editingSlide.text_animation_duration_ms) || 500,

                duration_ms: Number(editingSlide.duration_ms) || 5000,
                overlay_color: editingSlide.overlay_color || null,
                overlay_opacity: Number(editingSlide.overlay_opacity) || 0.35,
                image_fit: editingSlide.image_fit || "cover",
                subtitle_color: editingSlide.subtitle_color || null,
                subtitle_size: Number(editingSlide.subtitle_size) || 18,
                subtitle_font: editingSlide.subtitle_font || null,
            };

            if (editingSlide.id) {
                await api.put(`/slider/${editingSlide.id}`, payload);
                setNotification?.({ show: true, type: "success", message: "Slide atualizado!" });
            } else {
                await api.post("/slider", payload);
                setNotification?.({ show: true, type: "success", message: "Slide criado!" });
            }

            setEditingSlide(null);
            setRefreshTrigger(v => v + 1);

        } catch (err) {
            console.error("Erro salvar slide:", err);
            setNotification?.({
                show: true,
                type: "error",
                message: err?.response?.data?.message || err.message || "Erro ao salvar slide.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Delete handler
    const handleDeleteConfirmed = async () => {
        setShowConfirmModal(false);
        if (!slideToDelete) return;
        try {
            await api.delete(`/slider/${slideToDelete}`);
            setNotification?.({ show: true, type: "success", message: "Slide removido." });
            setSlideToDelete(null);
            setRefreshTrigger((v) => v + 1);
        } catch (err) {
            console.error("Erro ao remover slide:", err);
            setNotification?.({ show: true, type: "error", message: "Erro ao remover slide." });
        }
    };

    // create new slide template
    const handleNewSlide = () =>
        setEditingSlide({
            id: null,
            title: "Título Interno (SEO/Admin)",
            subtitle: "Subtítulo Opcional",
            display_text: "Seu Chamado Principal Aqui",
            image_url: "",
            link_url: "",
            button_text: "Acessar",
            status: "Rascunho",
            slide_order: slides.length + 1,
            transition_effect: "fade",
            border_radius_px: 16,
            animation_type: "none",
            text_animation_duration_ms: 500,
            duration_ms: 5000,
            overlay_color: "#000000",
            overlay_opacity: 0.35,
            image_fit: "cover",
            subtitle_color: "#ffffff",
            subtitle_size: 18,
            subtitle_font: "inherit",
            new_image_file: null,
            delete_image: false,
        });

    // handle file selection for preview and upload
    const onFileChange = (file) => {
        if (!file) return;
        const blobUrl = URL.createObjectURL(file);
        setEditingSlide((s) => ({
            ...s,
            new_image_file: file,
            image_url: blobUrl,
            delete_image: false,
        }));
    };

    // start editing an existing slide
    const startEdit = (slide) => {
        setEditingSlide({
            ...slide,
            duration_ms: Number(slide.duration_ms) || 5000,
            overlay_opacity: Number(slide.overlay_opacity ?? 0.35),
            overlay_color: slide.overlay_color || "#000000",
            image_fit: slide.image_fit || "cover",
            subtitle_color: slide.subtitle_color || "#ffffff",
            subtitle_size: Number(slide.subtitle_size) || 18,
            subtitle_font: slide.subtitle_font || "inherit",
            transition_effect: slide.transition_effect || "fade",
            border_radius_px: Number(slide.border_radius_px) || 16,
            animation_type: slide.animation_type || "none",
            text_animation_duration_ms: Number(slide.text_animation_duration_ms) || 500,
            display_text: slide.display_text || "", 
            new_image_file: null,
            delete_image: false,
        });
    };

    /* ------------------------
      Componente de Preview Dinâmico (Mantido com a correção do botão de remover)
      ------------------------ */
    const DynamicPreview = React.forwardRef(({ slide, previewKey, triggerPreview }, ref) => {
        // ... (Lógica de getFullImageUrl e estilos de transformação mantidos)
        if (!slide || (!slide.image_url && !slide.display_text)) return null; // Renderiza mesmo sem imagem se houver texto

        const imageUrl = getFullImageUrl(slide.image_url);
        const radius = `${slide.border_radius_px}px`;
        const effect = slide.transition_effect;
        const animation = slide.animation_type;
        const transitionDuration = TRANSITION_DURATION_MS; // 800ms

        const currentSlideRef = useRef(null);
        const prevSlideRef = useRef(null);
        const textWrapperRef = useRef(null);

        const isTransitioning = previewKey > 0;
        
        // Simulação dos estilos de transição (mantida)
        const getTransformations = (effect) => {
            let initialTransform = 'translateX(100%)';
            let finalTransform = 'translateX(0) scale(1) rotateY(0deg)';
            
            switch (effect) {
                case 'fade':
                    initialTransform = 'translateX(0) scale(1)';
                    break;
                case 'slide-left': initialTransform = 'translateX(100%)'; break;
                case 'slide-right': initialTransform = 'translateX(-100%)'; break;
                case 'zoom-in': initialTransform = 'scale(1.2)'; break;
                case 'cube': initialTransform = 'rotateY(90deg)'; finalTransform = 'rotateY(0deg)'; break;
                case 'flip': initialTransform = 'rotateY(180deg)'; finalTransform = 'rotateY(0deg)'; break;
                case 'stack': initialTransform = 'scale(1.05) translateY(5%)'; break;
                default: break;
            }

            return { initialTransform, finalTransform };
        };
        
        // Hook para aplicar as transformações no DOM (mantido)
        useEffect(() => {
            const currentImg = currentSlideRef.current;
            const prevImg = prevSlideRef.current;
            const textWrapper = textWrapperRef.current;

            if (!currentImg || !prevImg || !textWrapper) return;
            
            const { initialTransform, finalTransform } = getTransformations(effect);

            // 1. Configurar estado INICIAL (Repouso)
            if (!isTransitioning) {
                currentImg.style.transform = finalTransform;
                currentImg.style.opacity = 1;
                currentImg.style.zIndex = 5;

                prevImg.style.transform = initialTransform;
                prevImg.style.opacity = 0;
                prevImg.style.zIndex = 1;

                // Reset do Texto
                textWrapper.style.opacity = 1;
                textWrapper.style.transform = 'translate(0)';
                textWrapper.className = `text-white drop-shadow-xl w-full transition duration-[400ms]`;

            } else {
                // 2. Estado de TRANSIÇÃO (Dispara a animação)

                // A) PREPARAÇÃO
                currentImg.style.transition = 'none'; 
                currentImg.style.transform = initialTransform;
                currentImg.style.opacity = (effect === 'fade' || effect === 'zoom-in') ? 0 : 1; 
                currentImg.style.zIndex = 5; 
                
                prevImg.style.transition = 'none'; 
                prevImg.style.transform = 'translateX(0) scale(1)';
                prevImg.style.opacity = 1;
                prevImg.style.zIndex = 4;
                
                // C) PREPARAÇÃO (Texto: Esconde)
                textWrapper.style.transition = 'none';
                textWrapper.style.opacity = 0;
                textWrapper.style.transform = 'translate(0)'; 
                
                void currentImg.offsetWidth; // Força Reflow
                
                // D) EXECUÇÃO (Transição do Slide)
                setTimeout(() => {
                    // Novo slide entra
                    currentImg.style.transition = `opacity ${transitionDuration}ms ease, transform ${transitionDuration}ms ease`;
                    currentImg.style.transform = finalTransform;
                    currentImg.style.opacity = 1;
                    
                    // Slide anterior sai
                    prevImg.style.transition = `opacity ${transitionDuration}ms ease, transform ${transitionDuration}ms ease`;
                    let exitTransform = 'translateX(-100%)';
                    if (effect === 'cube' || effect === 'flip') exitTransform = `rotateY(-90deg)`;
                    if (effect === 'zoom-in') exitTransform = `scale(0.8)`;
                    if (effect === 'stack') exitTransform = `scale(0.9) translateY(0)`;

                    prevImg.style.transform = exitTransform;
                    prevImg.style.opacity = 0;

                    // Animação do Texto (entra no final da transição do slide)
                    setTimeout(() => {
                        const animationClass = (animation !== 'none') ? `animate-preview-${animation}` : '';
                        
                        // Aplica a duração da animação do texto
                        textWrapper.style.setProperty('--animation-duration', `${slide.text_animation_duration_ms}ms`);

                        // Força reset da classe
                        textWrapper.className = textWrapper.className.replace(/animate-preview-\S+/g, '');
                        void textWrapper.offsetWidth;

                        textWrapper.className = `text-white drop-shadow-xl w-full transition duration-[400ms] ${animationClass}`;
                        textWrapper.style.opacity = 1; 
                    }, transitionDuration * 0.4); 
                    
                }, 50); 
            }
            
        }, [isTransitioning, effect, imageUrl, animation, radius, slide.text_animation_duration_ms]);

        const containerStyle = { 
            height: "260px", 
            borderRadius: radius, 
            perspective: '1000px', 
            transformStyle: 'preserve-3d' 
        };

        const hasImage = !!slide.image_url;

        return (
            <div className="mt-3 p-3 bg-gray-100 rounded-lg border border-gray-300">
                <p className="text-sm font-medium mb-3 flex justify-between items-center text-gray-700">
                    Pré-visualização (Simulada)
                    <button 
                        onClick={triggerPreview} 
                        className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition flex items-center space-x-1"
                    >
                        ▶️ Testar Transição
                    </button>
                </p>

                <div
                    className="relative max-w-xl mt-3 overflow-hidden shadow-2xl mx-auto"
                    style={containerStyle}
                >
                    {/* 1. SLIDE ANTERIOR (Saindo) */}
                    <img
                        ref={prevSlideRef}
                        src="https://placehold.co/600x300/4f46e5/ffffff?text=Slide+Anterior" // Imagem placeholder
                        alt="Previous Slide"
                        className="absolute inset-0 w-full h-full"
                        style={{ objectFit: slide.image_fit, borderRadius: radius }}
                    />

                    {/* 2. SLIDE ATUAL (Entrando) - Usa fundo cinza se não houver imagem */}
                    <div
                        ref={currentSlideRef}
                        className="absolute inset-0 w-full h-full"
                        style={{
                            objectFit: slide.image_fit, 
                            borderRadius: radius,
                            backgroundColor: hasImage ? 'transparent' : '#e5e7eb', // Fundo cinza se sem imagem
                            backgroundImage: hasImage ? `url(${imageUrl})` : 'none',
                            backgroundSize: slide.image_fit,
                            backgroundPosition: 'center',
                        }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://placehold.co/600x300?text=Imagem+inválida";
                        }}
                    >
                        {/* Se não houver imagem, mostra um placeholder */}
                        {!hasImage && (
                            <div className="flex items-center justify-center w-full h-full text-gray-500 font-semibold text-lg">
                                SEM IMAGEM DE FUNDO
                            </div>
                        )}
                    </div>

                    {/* Overlay */}
                    <div
                        className="absolute inset-0 z-10 transition-all"
                        style={{ 
                            backgroundColor: slide.overlay_color, 
                            opacity: slide.overlay_opacity,
                            borderRadius: radius
                        }}
                    />

                    {/* Texto com Animação */}
                    <div className="absolute inset-0 flex items-center px-8 z-20">
                        <div ref={textWrapperRef} className="text-white drop-shadow-2xl w-full transition duration-[400ms] max-w-md">
                            
                            {/* TEXTO PRINCIPAL: display_text */}
                            <h3 className="text-3xl font-extrabold mb-2 leading-tight">
                                {slide.display_text || "[Título Principal]"} 
                            </h3>
                            
                            {/* Opcional: Mostrar Subtitle ou Title como texto secundário */}
                            {(slide.subtitle || slide.title) && (
                                <p
                                    className="text-lg mt-1"
                                    style={{
                                        color: slide.subtitle_color || "#fff",
                                        fontSize: slide.subtitle_size ? `${slide.subtitle_size}px` : undefined,
                                        fontFamily: slide.subtitle_font,
                                    }}
                                >
                                    {slide.subtitle || slide.title}
                                </p>
                            )}

                            {slide.link_url && (
                                <span className="inline-block mt-4 bg-indigo-600 px-4 py-2 rounded-lg text-white font-semibold text-sm shadow-md">
                                    {slide.button_text || "Acessar"}
                                </span>
                            )}
                        </div>
                    </div>
                    
                    {/* Botão de Remover Imagem */}
                    {hasImage && (
                        <button
                            className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full shadow-lg z-30 text-xs font-semibold hover:bg-red-700 transition"
                            onClick={() =>
                                setEditingSlide((s) => ({ ...s, image_url: "", new_image_file: null, delete_image: true }))
                            }
                        >
                            Remover Imagem
                        </button>
                    )}
                </div>
            </div>
        );
    });

    const renderPreview = () => (
        <DynamicPreview 
            // Key para forçar re-render em mudanças importantes
            key={editingSlide.transition_effect + editingSlide.animation_type + editingSlide.border_radius_px + editingSlide.text_animation_duration_ms} 
            slide={editingSlide} 
            previewKey={previewKey} 
            triggerPreview={triggerPreviewTransition} 
        />
    );


    /* ------------------------
      Render Edit Form
      ------------------------ */
    const renderEditForm = () => (
        <div className="p-8 bg-white rounded-2xl shadow-2xl border border-gray-100">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h3 className="text-2xl font-bold text-gray-800">{editingSlide?.id ? "✏️ Editar Slide Existente" : "✨ Criar Novo Slide"}</h3>
                <button onClick={() => setEditingSlide(null)} className="text-sm text-gray-500 hover:text-gray-800 transition">
                    ← Voltar à Lista
                </button>
            </div>

            {/* Pré-visualização na Coluna Lateral */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                    {/* SEÇÃO 1: TEXTO E ADMIN */}
                    <h4 className="text-xl font-semibold mb-3 text-indigo-700 border-b border-indigo-100 pb-2">Conteúdo Base e Status</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Título Interno (Admin/SEO)</label>
                            <input
                                className="p-2 border border-gray-300 rounded-lg w-full focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="Título Interno"
                                value={editingSlide?.title || ""}
                                onChange={(e) => setEditingSlide((s) => ({ ...s, title: e.target.value }))}
                            />
                        </div>

                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Status</label>
                            <select
                                className="p-2 border border-gray-300 rounded-lg w-full focus:border-indigo-500 focus:ring-indigo-500"
                                value={editingSlide?.status}
                                onChange={(e) => setEditingSlide((s) => ({ ...s, status: e.target.value }))}
                            >
                                <option value="Rascunho">Rascunho</option>
                                <option value="Publicado">Publicado</option>
                            </select>
                        </div>

                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Ordem (Prioridade)</label>
                            <input
                                type="number"
                                min="1"
                                className="p-2 border border-gray-300 rounded-lg w-full focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="Ex: 1"
                                value={editingSlide?.slide_order}
                                onChange={(e) => setEditingSlide((s) => ({ ...s, slide_order: Number(e.target.value) }))}
                            />
                        </div>
                    </div>
                    
                    {/* Campos de Texto Principal */}
                    <div className="mb-6 bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                        <label className="block text-lg font-bold mb-2 text-indigo-900">Texto Principal do Slide (*)</label>
                        <textarea
                            className="p-3 border border-indigo-300 rounded-lg w-full h-24 resize-none text-gray-800 focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="Este é o texto que aparecerá em destaque no banner."
                            value={editingSlide?.display_text || ""}
                            onChange={(e) => setEditingSlide((s) => ({ ...s, display_text: e.target.value }))}
                            required
                        />
                        
                        <label className="block text-sm font-medium mb-1 mt-4 text-indigo-900">Subtítulo (Opcional)</label>
                        <input
                            type="text"
                            className="p-2 border border-indigo-300 rounded-lg w-full text-gray-700 focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="Subtítulo opcional para o slide"
                            value={editingSlide?.subtitle || ""}
                            onChange={(e) => setEditingSlide((s) => ({ ...s, subtitle: e.target.value }))}
                        />
                    </div>
                    
                    {/* SEÇÃO 2: IMAGEM E AJUSTES */}
                    <h4 className="text-xl font-semibold mb-3 text-indigo-700 border-b border-indigo-100 pb-2">Mídia e Design</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="md:col-span-3">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Imagem de Fundo</label>
                            <label className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg cursor-pointer shadow hover:bg-indigo-700 transition">
                                📁 Selecionar Imagem
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => onFileChange(e.target.files?.[0])} />
                            </label>

                            <input
                                type="text"
                                className="p-2 border border-gray-300 rounded-lg w-full mt-3 text-gray-700 focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="Ou informe uma URL (Ex: /uploads/slider/arquivo.jpg)"
                                value={editingSlide?.image_url && !editingSlide.image_url.startsWith("blob:") ? editingSlide.image_url : ""}
                                onChange={(e) =>
                                    setEditingSlide((s) => ({ ...s, image_url: e.target.value, new_image_file: null, delete_image: false }))
                                }
                            />
                        </div>
                        
                        {/* CAMPO: RAIO DA BORDA */}
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700">Raio da Borda ({editingSlide?.border_radius_px}px)</label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="4"
                                className="w-full h-8"
                                value={editingSlide?.border_radius_px}
                                onChange={(e) => setEditingSlide((s) => ({ ...s, border_radius_px: Number(e.target.value) }))}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700">Ajuste da Imagem (Object Fit)</label>
                            <select className="p-2 border border-gray-300 rounded-lg w-full focus:border-indigo-500 focus:ring-indigo-500" value={editingSlide?.image_fit} onChange={(e) => setEditingSlide((s) => ({ ...s, image_fit: e.target.value }))}>
                                {fitOptions.map((f) => (
                                    <option key={f} value={f}>
                                        {f.charAt(0).toUpperCase() + f.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700">Duração do Slide ({editingSlide?.duration_ms}ms)</label>
                            <input
                                type="range"
                                min="1000"
                                max="10000"
                                step="500"
                                className="w-full h-8"
                                value={editingSlide?.duration_ms}
                                onChange={(e) => setEditingSlide((s) => ({ ...s, duration_ms: Number(e.target.value) }))}
                            />
                        </div>
                    </div>
                    
                    {/* SEÇÃO 3: ANIMAÇÕES E OVERLAY */}
                    <h4 className="text-xl font-semibold mb-3 text-indigo-700 border-b border-indigo-100 pb-2">Efeitos e Transições</h4>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700">Efeito de Transição (Slide)</label>
                            <select className="p-2 border border-gray-300 rounded-lg w-full focus:border-indigo-500 focus:ring-indigo-500" value={editingSlide?.transition_effect} onChange={(e) => setEditingSlide((s) => ({ ...s, transition_effect: e.target.value }))}>
                                {transitionOptions.map((ef) => (
                                    <option key={ef.value} value={ef.value}>{ef.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700">Animação de Entrada (Texto)</label>
                            <select className="p-2 border border-gray-300 rounded-lg w-full focus:border-indigo-500 focus:ring-indigo-500" value={editingSlide?.animation_type} onChange={(e) => setEditingSlide((s) => ({ ...s, animation_type: e.target.value }))}>
                                {animationOptions.map((an) => (
                                    <option key={an.value} value={an.value}>{an.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700">Tempo da Animação ({editingSlide?.text_animation_duration_ms}ms)</label>
                            <input
                                type="range"
                                min="200"
                                max="2000"
                                step="100"
                                className="w-full h-8"
                                value={editingSlide?.text_animation_duration_ms}
                                onChange={(e) => setEditingSlide((s) => ({ ...s, text_animation_duration_ms: Number(e.target.value) }))}
                            />
                        </div>
                    </div>
                    
                    {/* Overlay e Cores */}
                    <h4 className="text-xl font-semibold mb-3 text-indigo-700 border-b border-indigo-100 pb-2">Cores e Links</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        
                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Cor do Overlay</label>
                            <input type="color" className="w-full h-10 p-1 border border-gray-300 rounded-lg" value={editingSlide?.overlay_color || "#000000"} onChange={(e) => setEditingSlide((s) => ({ ...s, overlay_color: e.target.value }))} />
                        </div>

                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Opacidade do Overlay: {editingSlide?.overlay_opacity || 0.35}</label>
                            <input type="range" min="0" max="1" step="0.05" className="w-full h-8" value={editingSlide?.overlay_opacity} onChange={(e) => setEditingSlide((s) => ({ ...s, overlay_opacity: Number(e.target.value) }))} />
                        </div>
                        
                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Cor do Subtítulo</label>
                            <input type="color" className="w-full h-10 p-1 border border-gray-300 rounded-lg" value={editingSlide?.subtitle_color || "#ffffff"} onChange={(e) => setEditingSlide((s) => ({ ...s, subtitle_color: e.target.value }))} />
                        </div>

                        <input className="p-3 border border-gray-300 rounded-lg md:col-span-2 text-gray-700" placeholder="URL destino (https://...)" value={editingSlide?.link_url || ""} onChange={(e) => setEditingSlide((s) => ({ ...s, link_url: e.target.value }))} />

                        <input className="p-3 border border-gray-300 rounded-lg md:col-span-1 text-gray-700" placeholder="Texto do botão" value={editingSlide?.button_text || ""} onChange={(e) => setEditingSlide((s) => ({ ...s, button_text: e.target.value }))} />
                        
                    </div>
                    
                </div>
                
                <div className="lg:col-span-1">
                    {/* Renderiza a pré-visualização no canto */}
                    {editingSlide && renderPreview()}
                </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 border-t pt-4">
                <button onClick={() => setEditingSlide(null)} className="px-5 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition">
                    Cancelar
                </button>
                <button onClick={handleCreateOrUpdate} disabled={isSaving} className="px-5 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition shadow-md">
                    {isSaving ? "Salvando..." : "Salvar Slide"}
                </button>
            </div>
        </div>
    );

    /* ------------------------
      Render list (Tabela Melhorada)
      ------------------------ */
    const renderList = () => (
        <div className="p-8 bg-white rounded-2xl shadow-2xl border border-gray-100">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h3 className="text-2xl font-bold text-gray-800">🖼️ Gestão de Slides do Banner</h3>
                <button onClick={handleNewSlide} className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition">
                    + Novo Slide
                </button>
            </div>

            {loading ? (
                <div className="p-6 text-center text-gray-500">Carregando slides...</div>
            ) : slides.length === 0 ? (
                <div className="text-center p-6 text-gray-500">Nenhum slide cadastrado. Clique em "+ Novo Slide" para começar.</div>
            ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="p-3 text-left text-xs font-bold text-gray-600 uppercase">Ordem</th>
                                <th className="p-3 text-left text-xs font-bold text-gray-600 uppercase">Título Interno</th>
                                <th className="p-3 text-left text-xs font-bold text-gray-600 uppercase">Texto Principal</th>
                                <th className="p-3 text-left text-xs font-bold text-gray-600 uppercase">Status</th>
                                <th className="p-3 text-center text-xs font-bold text-gray-600 uppercase">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {slides.map((s) => (
                                <tr key={s.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                                    <td className="p-3 text-sm text-gray-900 font-semibold w-1/12">{s.slide_order}</td>
                                    <td className="p-3 text-sm text-gray-700 w-2/6">{s.title || s.subtitle || s.display_text || "Sem Título"}</td>
                                    <td className="p-3 text-sm text-gray-600 truncate max-w-xs">{s.display_text || "..."}</td>
                                    <td className="p-3 text-sm">
                                        <span className={getStatusBadge(s.status)}>
                                            {s.status}
                                        </span>
                                    </td>
                                    <td className="p-3 text-center w-1/4">
                                        <div className="flex justify-center space-x-3">
                                            <button 
                                                className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-50 transition" 
                                                title="Editar Slide"
                                                onClick={() => startEdit(s)}
                                            >
                                                <EditIcon />
                                            </button>
                                            <button
                                                className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition"
                                                title="Deletar Slide"
                                                onClick={() => {
                                                    setSlideToDelete(s.id);
                                                    setShowConfirmModal(true);
                                                }}
                                            >
                                                <TrashIcon />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    return (
        <>
            {editingSlide ? renderEditForm() : renderList()}

            <ConfirmationModal
                show={showConfirmModal}
                onClose={() => {
                    setShowConfirmModal(false);
                    setSlideToDelete(null);
                }}
                onConfirm={handleDeleteConfirmed}
                title="Confirmar Exclusão do Slide"
                message="Deseja realmente excluir este slide? Esta ação é irreversível."
                confirmText="Deletar"
            />
        </>
    );
}